import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useAppStore } from '@/stores/useAppStore';
import { fetchLatestBuildId, isNewBuildAvailable } from '@/lib/updateCheck';
import { noticeMessages } from '@/lib/errorMessages';

/** トーストのidを固定し、前面復帰・定期取得・preloadErrorが重ねて呼んでも一つしか出さない */
const UPDATE_TOAST_ID = 'update-available';

/** 前面復帰の直後を除く定期チェックの間隔（ADR-0011） */
const POLL_INTERVAL_MS = 10 * 60 * 1000;

function showUpdateToast(afterLoadError: boolean): void {
  const notice = noticeMessages.updateAvailable(afterLoadError);

  toast(notice.title, {
    id: UPDATE_TOAST_ID,
    description: notice.description,
    duration: Infinity,
    action: {
      label: notice.actionLabel,
      onClick: () => {
        window.location.reload();
      },
    },
  });
}

/**
 * デプロイされたビルドが、いま実行中のビルドと違うことを検知して案内する。
 *
 * 開きっぱなしのタブは自発的にリロードされず、Cloudflare PagesはSPAフォールバックにより
 * 古いチャンクの要求をindex.html（200・text/html）で返すため、動画生成の途中で読み込みに
 * 失敗する。検知しても動画生成中は案内を保留し、生成が終わってから出す（ADR-0011）。
 */
export function useUpdateNotifier(): void {
  const isGenerating = useAppStore((state) => state.generationStatus.isGenerating);
  // 生成中に検知した更新の保留状態。nullは保留なし、trueなら読み込み失敗を経由した検知
  const pendingAfterLoadErrorRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (import.meta.env.DEV) {
      return;
    }

    let cancelled = false;
    // 実行中のcheckがあれば新しいcheckを始めない（10分間隔とvisibilitychangeの二重取得を防ぐ）
    let inFlight: Promise<void> | null = null;

    const notify = (afterLoadError: boolean) => {
      pendingAfterLoadErrorRef.current = afterLoadError;
      if (!useAppStore.getState().generationStatus.isGenerating) {
        showUpdateToast(afterLoadError);
        pendingAfterLoadErrorRef.current = null;
      }
    };

    // version.jsonと照合して新版があれば案内する（ポーリング・前面復帰共通）
    const checkVersion = async () => {
      const latestBuildId = await fetchLatestBuildId();
      if (cancelled || !isNewBuildAvailable(__BUILD_ID__, latestBuildId)) {
        return;
      }
      notify(false);
    };

    const runExclusive = (task: () => Promise<void>) => {
      if (inFlight) {
        return;
      }
      inFlight = task().finally(() => {
        inFlight = null;
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        runExclusive(checkVersion);
      }
    };

    const handlePoll = () => {
      // 隠れている間は前面に戻ったときの検知で十分なので、ポーリングは何もしない
      if (document.visibilityState !== 'visible') {
        return;
      }
      runExclusive(checkVersion);
    };

    // チャンク読み込みの失敗自体がリロードで解決するので、version.jsonの照合はせず常に案内する
    const handlePreloadError = (event: Event) => {
      event.preventDefault();
      notify(true);
    };

    const intervalId = window.setInterval(handlePoll, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('vite:preloadError', handlePreloadError);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('vite:preloadError', handlePreloadError);
    };
  }, []);

  // 生成中に検知した更新は保留しておき、生成が終わった瞬間に案内する
  useEffect(() => {
    if (!isGenerating && pendingAfterLoadErrorRef.current !== null) {
      showUpdateToast(pendingAfterLoadErrorRef.current);
      pendingAfterLoadErrorRef.current = null;
    }
  }, [isGenerating]);
}
